/**
 * SecureEmbed Ingestion Source Registry
 *
 * Maps each datastore to its available ingestion sources.
 * Used by the IngestionWizard Step 2 (Sources) for source multi-select.
 *
 * Each source includes an emphasis tag indicating its domain strength:
 *   'general'  — broad multi-domain coverage
 *   'medical'  — medical, clinical, life sciences
 *   'science'  — physics, quantum computing, STEM
 *   'patent'   — patents and innovation
 *   'funding'  — grant-funded research
 */

export type SourceEmphasis = 'general' | 'medical' | 'science' | 'patent' | 'funding';

export interface IngestionSource {
  key: string;
  label: string;
  description: string;
  emphasis: SourceEmphasis;
  defaultEnabled: boolean;
}

const SOURCE_REGISTRY: Record<string, IngestionSource[]> = {
  scholarly: [
    // — General / Multi-domain —
    { key: 'openalex', label: 'OpenAlex', emphasis: 'general', description: 'Open scholarly metadata and citation graph — broad coverage across all academic disciplines', defaultEnabled: true },
    { key: 'semanticscholar', label: 'Semantic Scholar', emphasis: 'general', description: 'AI-curated academic paper database — strong citation analysis and influence scoring', defaultEnabled: true },
    { key: 'crossref', label: 'Crossref', emphasis: 'general', description: 'DOI registration and metadata — enriches records with publisher and citation data', defaultEnabled: true },
    { key: 'core', label: 'CORE', emphasis: 'general', description: 'Open access research papers aggregator — 200M+ papers from repositories worldwide', defaultEnabled: false },
    { key: 'base', label: 'BASE', emphasis: 'general', description: 'Bielefeld Academic Search Engine — 300M+ documents from 10,000+ content providers', defaultEnabled: false },
    { key: 'lens', label: 'Lens.org', emphasis: 'general', description: 'Unified scholarly + patent search — links research papers to patents and grants', defaultEnabled: false },
    // — Medical / Life Sciences —
    { key: 'pubmed', label: 'PubMed', emphasis: 'medical', description: 'NCBI biomedical literature — gold standard for clinical and medical research with MeSH indexing', defaultEnabled: true },
    { key: 'europepmc', label: 'EuropePMC', emphasis: 'medical', description: 'European life sciences literature — 40M+ abstracts from biomedical and health research', defaultEnabled: true },
    { key: 'biorxiv', label: 'BioRxiv / MedRxiv', emphasis: 'medical', description: 'Pre-print server for biology and medicine — cutting-edge research before peer review', defaultEnabled: false },
    { key: 'clinicaltrials', label: 'ClinicalTrials.gov', emphasis: 'medical', description: 'Clinical trial registry — completed trial outcomes, conditions, and interventions', defaultEnabled: false },
    { key: 'who_iris', label: 'WHO IRIS', emphasis: 'medical', description: 'World Health Organization publications — global health policy, epidemiology, and guidelines', defaultEnabled: false },
    { key: 'nih_reporter', label: 'NIH Reporter', emphasis: 'funding', description: 'NIH-funded research projects — medical grants with abstracts and award amounts', defaultEnabled: false },
    // — Physics / Quantum / STEM —
    { key: 'arxiv', label: 'arXiv', emphasis: 'science', description: 'Pre-print server for physics, maths, CS — primary source for quantum computing papers', defaultEnabled: false },
    { key: 'inspire_hep', label: 'INSPIRE-HEP', emphasis: 'science', description: 'High energy physics literature — quantum field theory, particle physics, and quantum information', defaultEnabled: false },
    { key: 'nasa_ads', label: 'NASA ADS', emphasis: 'science', description: 'Astrophysics Data System — physics, astronomy, and quantum mechanics literature', defaultEnabled: false },
    { key: 'dblp', label: 'DBLP', emphasis: 'science', description: 'Computer science bibliography — algorithms, quantum computing software, and CS theory', defaultEnabled: false },
    { key: 'nsf_awards', label: 'NSF Awards', emphasis: 'funding', description: 'National Science Foundation grants — quantum computing and STEM-funded research', defaultEnabled: false },
    // — Patents & Innovation —
    { key: 'uspto', label: 'USPTO', emphasis: 'patent', description: 'US Patent Office — medical device, pharmaceutical, quantum computing, and AI patents', defaultEnabled: false },
    { key: 'epo', label: 'EPO', emphasis: 'patent', description: 'European Patent Office — medical and computing patents via OPS API', defaultEnabled: false },
    { key: 'wipo', label: 'WIPO', emphasis: 'patent', description: 'World IP Organization — international PCT patent applications', defaultEnabled: false },
  ],
  medicine: [
    { key: 'pbs', label: 'PBS', emphasis: 'medical', description: 'Pharmaceutical Benefits Scheme', defaultEnabled: true },
    { key: 'tga', label: 'TGA', emphasis: 'medical', description: 'Therapeutic Goods Administration', defaultEnabled: true },
    { key: 'nps', label: 'NPS MedicineWise', emphasis: 'medical', description: 'NPS evidence-based medicines information', defaultEnabled: true },
  ],
  'food-interactions': [
    { key: 'foodb', label: 'FooDB', emphasis: 'general', description: 'Comprehensive food compound database', defaultEnabled: true },
    { key: 'usda', label: 'USDA', emphasis: 'general', description: 'US Department of Agriculture food data', defaultEnabled: true },
    { key: 'ausnut', label: 'AUSNUT', emphasis: 'general', description: 'Australian food composition database', defaultEnabled: true },
  ],
  aemo: [
    { key: 'aemo-api', label: 'AEMO API', emphasis: 'general', description: 'Australian Energy Market Operator data feeds', defaultEnabled: true },
  ],
  'aemo-duid': [
    { key: 'aemo-api', label: 'AEMO API', emphasis: 'general', description: 'AEMO DUID registration data', defaultEnabled: true },
  ],
  'aemo-fcas': [
    { key: 'aemo-api', label: 'AEMO API', emphasis: 'general', description: 'AEMO FCAS market data', defaultEnabled: true },
  ],
  'insurance-apra-grf': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'Australian Prudential Regulation Authority general reporting', defaultEnabled: true },
  ],
  'insurance-apra-lrf': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'APRA life insurance reporting', defaultEnabled: true },
  ],
  'insurance-apra-hrf': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'APRA health insurance reporting', defaultEnabled: true },
  ],
  'insurance-afca-complaints': [
    { key: 'afca', label: 'AFCA', emphasis: 'general', description: 'Australian Financial Complaints Authority data', defaultEnabled: true },
  ],
  'insurance-premium-adequacy': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'Premium adequacy regulatory data', defaultEnabled: true },
  ],
  'insurance-claims-triage': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'Claims triage regulatory data', defaultEnabled: true },
  ],
  'insurance-actuarial-triangles': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'Actuarial triangle reporting data', defaultEnabled: true },
  ],
  'insurance-bordereaux': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'Cedant bordereaux data', defaultEnabled: true },
  ],
  'insurance-state-schemes': [
    { key: 'apra', label: 'APRA', emphasis: 'general', description: 'State scheme regulatory data', defaultEnabled: true },
  ],
  'insurance-climate-projections': [
    { key: 'bom', label: 'BOM', emphasis: 'science', description: 'Bureau of Meteorology climate projection data', defaultEnabled: true },
    { key: 'csiro', label: 'CSIRO', emphasis: 'science', description: 'CSIRO climate science projections', defaultEnabled: false },
  ],
  'insurance-cyber-threats': [
    { key: 'asd', label: 'ASD', emphasis: 'general', description: 'Australian Signals Directorate threat data', defaultEnabled: true },
  ],
  'insurance-bom-weather': [
    { key: 'bom', label: 'BOM', emphasis: 'general', description: 'Bureau of Meteorology weather data', defaultEnabled: true },
  ],
  bom: [
    { key: 'bom-api', label: 'BOM API', emphasis: 'general', description: 'Bureau of Meteorology observation data', defaultEnabled: true },
  ],
  aer: [
    { key: 'aer-api', label: 'AER', emphasis: 'general', description: 'Australian Energy Regulator data', defaultEnabled: true },
  ],
  regulatory: [
    { key: 'legislation', label: 'Legislation', emphasis: 'general', description: 'Commonwealth and state legislation', defaultEnabled: true },
  ],
  partner: [
    { key: 'partner-api', label: 'Partner API', emphasis: 'science', description: 'Partner and quantum discovery data', defaultEnabled: true },
  ],
  curriculum: [
    { key: 'acara', label: 'ACARA', emphasis: 'general', description: 'Australian Curriculum, Assessment and Reporting Authority', defaultEnabled: true },
  ],
  'education-resources': [
    { key: 'acara', label: 'ACARA', emphasis: 'general', description: 'Australian curriculum resources', defaultEnabled: true },
    { key: 'university-apis', label: 'University APIs', emphasis: 'general', description: 'Australian university data feeds', defaultEnabled: false },
  ],
  'tertiary-resources': [
    { key: 'university-apis', label: 'University APIs', emphasis: 'general', description: 'University and research institution data', defaultEnabled: true },
    { key: 'arc', label: 'ARC', emphasis: 'funding', description: 'Australian Research Council grants and publications', defaultEnabled: false },
  ],
  innovateflow: [
    { key: 'innovateflow-api', label: 'InnovateFlow API', emphasis: 'general', description: 'InnovateFlow application data', defaultEnabled: true },
  ],
  'theology-articles': [
    { key: 'openalex', label: 'OpenAlex', emphasis: 'general', description: '~990K theology articles, CC0, free API key', defaultEnabled: true },
    { key: 'crossref', label: 'CrossRef', emphasis: 'general', description: '~497K theology DOIs, polite API', defaultEnabled: true },
    { key: 'doaj', label: 'DOAJ', emphasis: 'general', description: '~65K guaranteed OA, 276 theology journals', defaultEnabled: true },
    { key: 'unpaywall', label: 'Unpaywall', emphasis: 'general', description: 'OA full-text discovery (~25% hit rate)', defaultEnabled: true },
    { key: 'core', label: 'CORE', emphasis: 'general', description: '50K-150K theology full-text from IRs', defaultEnabled: false },
    { key: 'semantic_scholar', label: 'Semantic Scholar', emphasis: 'general', description: '100K-300K theology papers', defaultEnabled: false },
    { key: 'jstor_dfr', label: 'JSTOR DfR', emphasis: 'general', description: '200K+ biblical/theological metadata', defaultEnabled: false },
    { key: 'internet_archive', label: 'Internet Archive / Fatcat', emphasis: 'general', description: 'Public domain theological works', defaultEnabled: false },
  ],
};

/**
 * Get available ingestion sources for a given datastore.
 * Returns an empty array for datastores without configured sources.
 */
export function getSourcesForDatastore(datastoreName: string): IngestionSource[] {
  return SOURCE_REGISTRY[datastoreName] || [];
}

/**
 * Get all datastore keys that have configured sources.
 */
export function getDatastoresWithSources(): string[] {
  return Object.keys(SOURCE_REGISTRY);
}
